import React, { useEffect, useRef, useId, useState } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import IosSteps from './IosSteps';
import './notifications.css';

// The second ask, when someone closes the sheet
const MEMBER_CONFIRM = "Without notifications you could miss a waitlist place, or only find out a session is cancelled when you arrive. They take a moment to turn on, and you choose which ones you get.";
const STAFF_CONFIRM = "Without notifications you won't hear about late changes, registers to take or incidents until you next open the app. They take a moment to turn on, and you choose which ones you get.";

const STAFF_COPY = {
  heading: "Know what's happening at the club",
  intro: 'Get a notification when:',
  ticks: ["The register hasn't been taken", 'A new member joins', 'An incident report is filed'],
};
const MEMBER_COPY = {
  heading: "Don't miss a place",
  intro: 'Get a notification when:',
  ticks: [
    'A waitlist place opens, so you can book it before someone else does',
    "A session you've booked is cancelled",
    'A payment needs you, or the club posts news',
  ],
};

const Tick = () => <CheckIcon className="notif-sheet__tick" width="18" height="18" aria-hidden="true" />;

const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * kind: 'ready' (turn on now), 'ios-install' (Home Screen first) or
 *       'ios-old' (update iOS)
 * onClose(): any way of putting the sheet away, which counts as "not now"
 * onGotIt(): "Got it" or "OK", which closes without counting
 * onChoose(): "Choose" (which notifications), to the Notifications page
 */
export default function NotificationPromptSheet({ kind, staff, browser, loading, error, onTurnOn, onClose, onGotIt, onChoose }) {
  const headingId = useId();
  const sheetRef = useRef(null);
  const mainRef = useRef(null);
  // Closing asks once more ("Are you sure?"); closing again really closes.
  // The update-your-iPhone message just closes.
  const [confirming, setConfirming] = useState(false);
  const tryClose = () => {
    if (kind === 'ios-old' || confirming) onClose();
    else setConfirming(true);
  };
  const onCloseRef = useRef(tryClose);
  onCloseRef.current = tryClose;
  // The main button changes with the step, so focus follows it
  useEffect(() => {
    if (confirming && mainRef.current) mainRef.current.focus();
  }, [confirming]);

  // Focus goes into the sheet and comes back afterwards; the page behind
  // doesn't scroll while it's open. Escape and the Tab trap listen on the
  // document, so they work even when focus is on the page or on a button
  // that is disabled while turning on.
  useEffect(() => {
    const previous = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    if (mainRef.current) mainRef.current.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && sheetRef.current) {
        const items = Array.from(sheetRef.current.querySelectorAll(FOCUSABLE));
        if (!items.length) { e.preventDefault(); return; }
        const first = items[0];
        const last = items[items.length - 1];
        const inside = sheetRef.current.contains(document.activeElement);
        if (!inside) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) previous.focus();
    };
  }, []);

  let heading;
  let body;
  let main;
  if (kind === 'ios-old') {
    heading = 'Update your iPhone for alerts';
    body = <p className="notif-sheet__text">Notifications need iOS 16.4 or newer. Update in Settings, then General, then Software Update, and come back to turn them on.</p>;
    main = <button type="button" ref={mainRef} className="notif-sheet__primary" onClick={onGotIt}>Got it</button>;
  } else if (kind === 'ios-install') {
    heading = 'Get alerts on your iPhone';
    body = (
      <>
        <p className="notif-sheet__text">So we can tell you when a waitlist place opens or a session is cancelled. iPhones need the app on your Home Screen first. It takes 20 seconds:</p>
        <IosSteps browser={browser === 'chrome' ? 'chrome' : 'safari'} />
      </>
    );
    main = <button type="button" ref={mainRef} className="notif-sheet__primary" onClick={onGotIt}>Got it</button>;
  } else {
    const copy = staff ? STAFF_COPY : MEMBER_COPY;
    heading = copy.heading;
    body = (
      <>
        <p className="notif-sheet__text">{copy.intro}</p>
        <ul className="notif-sheet__ticks">
          {copy.ticks.map((t) => <li key={t}><Tick /><span>{t}</span></li>)}
        </ul>
      </>
    );
    main = (
      <button type="button" ref={mainRef} className="notif-sheet__primary" onClick={onTurnOn} disabled={loading}>
        {loading ? 'Turning on…' : 'Turn on notifications'}
      </button>
    );
  }

  if (confirming) {
    heading = 'Are you sure?';
    body = <p className="notif-sheet__text">{staff ? STAFF_CONFIRM : MEMBER_CONFIRM}</p>;
    main = kind === 'ios-install' ? (
      <button type="button" ref={mainRef} className="notif-sheet__primary" onClick={() => setConfirming(false)}>Show me how</button>
    ) : (
      <button type="button" ref={mainRef} className="notif-sheet__primary" onClick={onTurnOn} disabled={loading}>
        {loading ? 'Turning on…' : 'Turn on notifications'}
      </button>
    );
  }

  return (
    <div className="notif-backdrop" data-testid="notif-backdrop" onClick={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div
        className="notif-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        ref={sheetRef}
      >
        <div className="notif-sheet__handle" aria-hidden="true" />
        <div className="notif-sheet__top">
          <div className="notif-sheet__tile"><BellIcon width="30" height="30" aria-hidden="true" /></div>
          <button type="button" className="notif-sheet__close" aria-label="Close" onClick={tryClose}>
            <XMarkIcon width="20" height="20" aria-hidden="true" />
          </button>
        </div>
        <h2 id={headingId} className="notif-sheet__heading">{heading}</h2>
        {body}
        {error && <p className="notif-sheet__error" role="alert">{error}</p>}
        {main}
        {kind !== 'ios-old' && onChoose && !confirming && (
          <p className="notif-sheet__text" style={{ margin: '12px 0 0', textAlign: 'center' }}>You can choose which ones you get.</p>
        )}
        {kind !== 'ios-old' && onChoose && (
          <button type="button" className="notif-sheet__choose" onClick={onChoose} aria-label="Choose which notifications">Choose</button>
        )}
        {kind !== 'ios-old' && (
          <button type="button" className="notif-sheet__later" onClick={tryClose}>{confirming ? 'Skip for now' : 'Not now'}</button>
        )}
      </div>
    </div>
  );
}
